import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "../../lib/api";
import { Reveal } from "../../lib/reveal";
import { CATEGORY_LABELS } from "../../data/content";

const ChantiersCarousel = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);

  const scrollBy = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.querySelector("[data-slide]");
    const step = slide ? slide.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  useEffect(() => {
    api
      .get("/projects")
      .then((res) => setProjects(res.data || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="border-t border-black/10 bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="aspect-[4/3] animate-pulse bg-bati-mist" />
        </div>
      </section>
    );
  }
  if (!projects.length) return null;

  return (
    <section data-testid="chantiers-section" className="border-t border-black/10 bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="mb-4 flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.22em] text-bati-sky">
              <span className="h-px w-10 bg-bati-sky" /> Sur le terrain
            </p>
            <h2 className="font-display text-4xl font-extrabold tracking-tight text-bati-ink sm:text-5xl">Nos chantiers</h2>
          </Reveal>
          <Reveal delay={0.15} className="flex items-center gap-3">
            <button
              onClick={() => scrollBy(-1)}
              data-testid="chantiers-prev-button"
              aria-label="Chantier précédent"
              className="grid h-12 w-12 place-items-center border border-black/15 text-bati-ink transition-colors duration-300 hover:border-bati-blue hover:bg-bati-blue hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              data-testid="chantiers-next-button"
              aria-label="Chantier suivant"
              className="grid h-12 w-12 place-items-center border border-black/15 text-bati-ink transition-colors duration-300 hover:border-bati-blue hover:bg-bati-blue hover:text-white"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          </Reveal>
        </div>
      </div>

      <Reveal>
        <div
          ref={trackRef}
          data-testid="chantiers-carousel"
          className="flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-5 [-ms-overflow-style:none] [scrollbar-width:none] lg:px-[max(2rem,calc((100vw-80rem)/2+2rem))] [&::-webkit-scrollbar]:hidden"
        >
          {projects.map((p) => (
            <Link
              key={p.id}
              data-slide
              to={`/realisations/${p.slug}`}
              data-testid={`chantier-slide-${p.slug}`}
              draggable={false}
              className="group relative block w-[82%] shrink-0 snap-start overflow-hidden bg-bati-mist sm:w-[58%] lg:w-[38%]"
            >
              <div className="aspect-[4/3] w-full overflow-hidden">
                <img
                  src={p.cover_image}
                  alt={`${p.title} — ${p.city}`}
                  loading="lazy"
                  draggable={false}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                />
              </div>
              <span className="absolute left-4 top-4 bg-white/95 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-bati-blue">
                {CATEGORY_LABELS[p.category] || p.category}
              </span>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-5 pt-20">
                <h3 className="font-display text-lg font-bold leading-tight text-white sm:text-xl">{p.title}</h3>
                <p className="mt-1.5 line-clamp-2 text-[13px] font-medium text-white/75">
                  {p.work_types || p.description}
                </p>
                <p className="mt-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-bati-sky">
                  {p.city}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </p>
              </div>
            </Link>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

export default ChantiersCarousel;
